import React, { useState, useCallback, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import type { QuickChartData, WidgetInstance, WidgetSourceMetadata } from '@trinity/chat';
import { SegmentationDashboardWidget } from '@trinity/chat';
import { useChartWidgetApi } from '@trinity/chat-components/containers/ChartWidget';
import { useCurrentSegmentIdsContext } from '@trinity/chat-components/containers/Segmentation.Dashboard/context';
import { useWidgetMapContext } from '@trinity/chat-components/containers/WidgetGrid/context';
import Input from '../Input';
import { Header, Modal } from '../Modal';
import ButtonSet from '../Modal/ButtonSet';
import { ButtonActivityIndicator } from '../presentation';
import Button from '../presentation/Button';
import { ChartPreviewRenderer } from './ChartPreviewRenderer';

type ChartSaveModalProps = {
  open: boolean;
  onClose: () => void;
  chartData: QuickChartData;
  chartTitle: string;
  selectedThemeId?: number | null;
  colorOverrides?: Record<string, string> | null;
  labelOverrides?: Record<string, string> | null;
  sourceMetadata?: Omit<WidgetSourceMetadata, 'createdAt'>;
  onSaveSuccess: (newWidget: WidgetInstance) => void;
  disablePortal?: boolean;
};

export const ChartSaveModal = ({
  open,
  onClose,
  chartData,
  chartTitle,
  selectedThemeId,
  colorOverrides,
  labelOverrides,
  sourceMetadata,
  onSaveSuccess,
  disablePortal,
}: ChartSaveModalProps) => {
  const { projectId } = useCurrentSegmentIdsContext();
  const widgetMap = useWidgetMapContext();
  const [saveTitle, setSaveTitle] = useState('');
  const [saveTouched, setSaveTouched] = useState(false);
  const [titleInitialized, setTitleInitialized] = useState(false);

  // Use the API context
  const { useSaveWidget } = useChartWidgetApi();
  const saveWidgetMutation = useSaveWidget();

  // Handle success separately to maintain the same interface
  useEffect(() => {
    if (saveWidgetMutation.isSuccess && saveWidgetMutation.data) {
      onSaveSuccess(saveWidgetMutation.data);
      onClose();
    }
  }, [saveWidgetMutation.isSuccess, saveWidgetMutation.data, onSaveSuccess, onClose]);

  // Initialize title when modal opens
  useEffect(() => {
    if (open && !titleInitialized) {
      setSaveTitle(chartTitle);
      setSaveTouched(false);
      setTitleInitialized(true);
    } else if (!open) {
      setTitleInitialized(false);
    }
  }, [open, chartTitle, titleInitialized]);

  const handleClose = useCallback(() => {
    setSaveTitle('');
    setSaveTouched(false);
    setTitleInitialized(false);
    saveWidgetMutation.reset();
    onClose();
  }, [onClose, saveWidgetMutation]);

  const handleSaveTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSaveTitle(e.target.value);
    setSaveTouched(true);
  }, []);

  const handleSaveChart = () => {
    const title =
      chartData.type === 'kpi-widget' ? chartData.title?.trim() || 'KPI Widget' : saveTitle.trim();
    saveWidgetMutation.mutate({
      projectId,
      title,
      data: {
        type: SegmentationDashboardWidget.ChartVisualization,
        props: {
          chart: chartData,
          selectedThemeId,
          colorOverrides,
          labelOverrides,
        },
      },
      sourceMetadata: sourceMetadata
        ? ({
            ...sourceMetadata,
            createdAt: new Date().toISOString(),
          } as WidgetSourceMetadata)
        : undefined,
    });
  };

  // Validation logic
  const trimmedSaveTitle = saveTitle.trim();
  const isKpiWidget = chartData.type === 'kpi-widget';
  const titleLength = trimmedSaveTitle.length;
  const minLen = 3;
  const lengthValid = isKpiWidget || titleLength >= minLen;
  const canSave =
    isKpiWidget || (lengthValid && trimmedSaveTitle.length > 0 && !saveWidgetMutation.isPending);
  const showError = saveTouched && !lengthValid;
  let errorMessage = '';
  if (showError && titleLength < minLen) {
    errorMessage = `Title must be at least ${minLen} characters.`;
  }

  const PreviewComponent = useMemo(() => {
    const Comp = widgetMap[SegmentationDashboardWidget.ChartVisualization];
    return Comp ? <Comp {...{ chart: chartData, selectedThemeId, colorOverrides, labelOverrides }} /> : null;
  }, [widgetMap, chartData, selectedThemeId, colorOverrides, labelOverrides]);

  return (
    <Modal open={open} onClose={handleClose} className="quant-save-modal" disablePortal={disablePortal}>
      <div style={{ minWidth: 400, maxWidth: 600 }}>
        <Header>Save Chart</Header>
        {!isKpiWidget && (
          <ModalSection>
            <ModalLabel>Title</ModalLabel>
            <Input
              name="chart-save-title"
              type="text"
              value={saveTitle}
              onChange={handleSaveTitleChange}
              style={{ width: '100%' }}
            />
            <FieldMeta>{errorMessage && <FieldError>{errorMessage}</FieldError>}</FieldMeta>
          </ModalSection>
        )}
        <ModalSection>
          <ModalLabel>Preview</ModalLabel>
          <PreviewContainer>
            <ChartPreviewRenderer chartData={chartData}>{PreviewComponent}</ChartPreviewRenderer>
          </PreviewContainer>
        </ModalSection>
        <ButtonSet>
          <Button.Destructive variant="brick" onClick={handleClose}>
            Cancel
          </Button.Destructive>
          <ButtonActivityIndicator
            indicatorSize={12}
            disabled={!canSave}
            implicitDisable={saveWidgetMutation.isPending}
            loading={saveWidgetMutation.isPending}
            variant="brick"
            onClick={handleSaveChart}
          >
            Save
          </ButtonActivityIndicator>
        </ButtonSet>
      </div>
    </Modal>
  );
};

const ModalSection = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  marginBottom: '16px',
});

const ModalLabel = styled.div(({ theme }) => ({
  fontSize: 18,
  fontFamily: theme.fonts.semiBold,
  color: theme.palette.text.primary,
}));

const PreviewContainer = styled.div({
  border: '1px solid var(--gray-l)',
  borderRadius: 8,
  padding: 12,
  background: 'white',
  minHeight: 240,
  overflow: 'hidden',
});

const FieldMeta = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  marginTop: '4px',
});

const FieldError = styled.span({
  fontSize: 11,
  color: 'var(--red-m)',
  fontWeight: 600,
});
